/**
 * Widget Library Interface
 * Unified widget library using the configuration system
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { WIDGET_TYPES } from './UnifiedWidgetSystem';
import { WidgetConfigurator } from './WidgetConfigurator';

// Library Container
const LibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${theme.colors.background.default};
  border-radius: ${theme.borderRadius.lg};
  max-height: 80vh;
  overflow-y: auto;
`;

const LibraryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const LibraryTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  width: 200px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.main}20;
  }
`;

// Widget Type Grid
const WidgetTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const WidgetTypeCard = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const WidgetIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${theme.colors.primary.main};
`;

const WidgetName = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const WidgetDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.4;
`;

// Template Section
const TemplateSection = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TemplateCard = styled.div`
  padding: 1rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const TemplateName = styled.div`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const TemplateDescription = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const TemplateConfig = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  font-family: 'Monaco', 'Consolas', monospace;
  background: ${theme.colors.background.default};
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Widget Templates
const WIDGET_TEMPLATES = {
  digital: [
    {
      name: 'Speed Display',
      description: 'Large speed readout with trend indicator',
      config: {
        displayFormat: 'single',
        precision: 1,
        showTrend: true,
        showStatus: true
      },
      dataPaths: ['navigation.speedOverGround'],
      title: 'Speed',
      unit: 'kts'
    },
    {
      name: 'GPS Position',
      description: 'Latitude and longitude coordinates',
      config: {
        displayFormat: 'multi',
        precision: 6,
        values: [
          { label: 'LAT', dataPath: 'navigation.position.latitude', unit: '°', color: '#10b981' },
          { label: 'LON', dataPath: 'navigation.position.longitude', unit: '°', color: '#10b981' }
        ]
      },
      dataPaths: ['navigation.position.latitude', 'navigation.position.longitude'],
      title: 'GPS Position',
      unit: ''
    },
    {
      name: 'Engine Monitor',
      description: 'Multi-value engine parameters',
      config: {
        displayFormat: 'multi',
        values: [
          { label: 'RPM', dataPath: 'propulsion.main.revolutions', unit: '', color: '#10b981' },
          { label: 'Temp', dataPath: 'propulsion.main.temperature', unit: '°F', color: '#f59e0b' },
          { label: 'Oil', dataPath: 'propulsion.main.oilPressure', unit: 'psi', color: '#10b981' }
        ]
      },
      dataPaths: ['propulsion.main.revolutions', 'propulsion.main.temperature', 'propulsion.main.oilPressure'],
      title: 'Engine Monitor',
      unit: ''
    }
  ],
  gauge: [
    {
      name: 'Engine RPM',
      description: 'Circular RPM gauge with zones',
      config: {
        gaugeType: 'circular',
        min: 0,
        max: 4000,
        showDigitalOverlay: true,
        zones: [
          { min: 0, max: 800, color: '#ef4444', label: 'Idle' },
          { min: 800, max: 2500, color: '#10b981', label: 'Normal' },
          { min: 2500, max: 3500, color: '#f59e0b', label: 'High' },
          { min: 3500, max: 4000, color: '#ef4444', label: 'Redline' }
        ]
      },
      dataPaths: ['propulsion.main.revolutions'],
      title: 'Engine RPM',
      unit: 'RPM'
    },
    {
      name: 'Battery Voltage',
      description: 'Linear voltage gauge with thresholds',
      config: {
        gaugeType: 'linear',
        min: 10,
        max: 15,
        showDigitalOverlay: true,
        zones: [
          { min: 10, max: 12, color: '#ef4444', label: 'Low' },
          { min: 12, max: 13, color: '#f59e0b', label: 'Caution' },
          { min: 13, max: 15, color: '#10b981', label: 'Normal' }
        ]
      },
      dataPaths: ['electrical.batteries.house.voltage'],
      title: 'Battery Voltage',
      unit: 'V'
    },
    {
      name: 'Compass Heading',
      description: 'Compass rose with heading display',
      config: {
        gaugeType: 'compass',
        showRose: true,
        showDigitalOverlay: true
      },
      dataPaths: ['navigation.headingTrue'],
      title: 'Heading',
      unit: '°'
    }
  ],
  bar: [
    {
      name: 'Fuel Tank Level',
      description: 'Vertical tank level with zones',
      config: {
        orientation: 'vertical',
        barType: 'tank',
        min: 0,
        max: 100,
        showScale: true,
        zones: [
          { min: 0, max: 10, color: '#ef4444', label: 'Critical' },
          { min: 10, max: 25, color: '#f59e0b', label: 'Low' },
          { min: 25, max: 100, color: '#10b981', label: 'Normal' }
        ]
      },
      dataPaths: ['tanks.fuel.main.currentLevel'],
      title: 'Fuel Tank',
      unit: '%'
    },
    {
      name: 'Battery Charge',
      description: 'Horizontal battery charge indicator',
      config: {
        orientation: 'horizontal',
        barType: 'battery',
        min: 0,
        max: 100,
        showScale: true,
        zones: [
          { min: 0, max: 20, color: '#ef4444', label: 'Low' },
          { min: 20, max: 50, color: '#f59e0b', label: 'Medium' },
          { min: 50, max: 100, color: '#10b981', label: 'Full' }
        ]
      },
      dataPaths: ['electrical.batteries.house.stateOfCharge'],
      title: 'Battery SOC',
      unit: '%'
    }
  ],
  chart: [
    {
      name: 'Engine Temperature Trend',
      description: 'Line chart showing temperature over time',
      config: {
        chartType: 'line',
        timeRange: '6h',
        showGrid: true,
        showLegend: true
      },
      dataPaths: ['propulsion.main.temperature'],
      title: 'Engine Temperature',
      unit: '°F'
    },
    {
      name: 'Battery Voltage History',
      description: 'Historical voltage trend',
      config: {
        chartType: 'line',
        timeRange: '24h',
        showGrid: true,
        showLegend: false
      },
      dataPaths: ['electrical.batteries.house.voltage'],
      title: 'Battery History',
      unit: 'V'
    }
  ]
};

/**
 * Widget Library Component
 */
export const WidgetLibrary = ({
  onWidgetSelect = () => {},
  onClose = () => {},
  availableDataPaths = [],
  mode = 'library' // 'library' or 'configurator'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [configuratorConfig, setConfiguratorConfig] = useState({});

  // Filter widget types based on search
  const filteredWidgetTypes = Object.entries(WIDGET_TYPES).filter(([type, config]) =>
    config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle widget type selection
  const handleWidgetTypeSelect = (type) => {
    if (mode === 'library') {
      setSelectedType(type);
      setShowConfigurator(true);
      setConfiguratorConfig({
        type,
        config: WIDGET_TYPES[type].defaultConfig,
        dataPaths: [],
        title: 'New Widget',
        unit: ''
      });
    } else {
      onWidgetSelect({ type, config: WIDGET_TYPES[type].defaultConfig });
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    if (mode === 'library') {
      setShowConfigurator(true);
      setConfiguratorConfig(template);
    } else {
      onWidgetSelect(template);
    }
  };

  // Handle configurator save
  const handleConfiguratorSave = () => {
    onWidgetSelect(configuratorConfig);
    setShowConfigurator(false);
    onClose();
  };

  // Handle configurator cancel
  const handleConfiguratorCancel = () => {
    setShowConfigurator(false);
    setSelectedType(null);
  };

  if (showConfigurator) {
    return (
      <WidgetConfigurator
        initialConfig={configuratorConfig}
        availableDataPaths={availableDataPaths}
        onConfigChange={setConfiguratorConfig}
        onSave={handleConfiguratorSave}
        onCancel={handleConfiguratorCancel}
        showPreview={true}
      />
    );
  }

  return (
    <LibraryContainer>
      <LibraryHeader>
        <LibraryTitle>Widget Library</LibraryTitle>
        <SearchInput
          type="text"
          placeholder="Search widgets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </LibraryHeader>

      {/* Widget Types */}
      <div>
        <SectionTitle>Widget Types</SectionTitle>
        <WidgetTypeGrid>
          {filteredWidgetTypes.map(([type, config]) => (
            <WidgetTypeCard
              key={type}
              onClick={() => handleWidgetTypeSelect(type)}
            >
              <WidgetIcon>{config.icon}</WidgetIcon>
              <WidgetName>{config.name}</WidgetName>
              <WidgetDescription>{config.description}</WidgetDescription>
            </WidgetTypeCard>
          ))}
        </WidgetTypeGrid>
      </div>

      {/* Widget Templates */}
      {Object.entries(WIDGET_TEMPLATES).map(([type, templates]) => (
        <TemplateSection key={type}>
          <SectionTitle>{WIDGET_TYPES[type]?.name} Templates</SectionTitle>
          <TemplateGrid>
            {templates.map((template, index) => (
              <TemplateCard
                key={index}
                onClick={() => handleTemplateSelect({ ...template, type })}
              >
                <TemplateName>{template.name}</TemplateName>
                <TemplateDescription>{template.description}</TemplateDescription>
                <TemplateConfig>
                  {template.dataPaths.join(', ')}
                </TemplateConfig>
              </TemplateCard>
            ))}
          </TemplateGrid>
        </TemplateSection>
      ))}
    </LibraryContainer>
  );
};

export default WidgetLibrary;